import React, { useState, useEffect } from 'react';
import Seo from '../components/Seo';
import { seo } from '../seo/seoData';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = import.meta.env.VITE_BACKEND_URL + '/api';

// Phase C — public client Devis validation page.
// Opened from the secure link in the Devis email (/devis/validate/:token).
// GET fetches a minimal summary; POST accepts (validates) the devis. The token
// is the credential; the page never reveals whether a devis exists for an
// invalid/expired/used link (one generic message).
const DevisValidatePage = () => {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | review | done | invalid
  const [devis, setDevis] = useState(null);
  const [already, setAlready] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/public/commercial/devis/validate/${token}`);
        if (cancelled) return;
        if (res.data?.already_validated) {
          setDevis(res.data); setAlready(true); setPhase('done');
        } else {
          setDevis(res.data); setPhase('review');
        }
      } catch (e) {
        if (!cancelled) setPhase('invalid');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  const handleValidate = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/public/commercial/devis/validate/${token}`);
      setAlready(Boolean(res.data?.already_validated));
      setDevis(prev => ({ ...(prev || {}), ...(res.data || {}) }));
      setPhase('done');
    } catch (e) {
      setError("Ce lien n'est plus valide. Contactez-nous pour un nouveau devis.");
    }
    setSubmitting(false);
  };

  const fmtMoney = (v, cur) =>
    (typeof v === 'number')
      ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: cur || 'EUR' }).format(v)
      : '—';
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return '—'; }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', padding: 20 }}>
      <Seo {...seo.devisValidate} />
      <div style={{ background: '#141414', border: '1px solid #2a2a2a', borderRadius: 20, padding: 40, width: 460, maxWidth: '92vw', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: 1 }}>BLACK MATTE</div>
          <div style={{ fontSize: 11, color: '#C9A24B', letterSpacing: 3, marginTop: 3 }}>DEVIS</div>
        </div>

        {phase === 'loading' && (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 14, padding: '20px 0' }}>Chargement…</div>
        )}

        {phase === 'invalid' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 14 }}>⚠️</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: '#fff', marginBottom: 8 }}>Lien invalide ou expiré</div>
            <div style={{ color: '#9ca3af', fontSize: 14 }}>Ce lien de validation n'est plus valable. Contactez-nous pour recevoir un nouveau devis.</div>
          </div>
        )}

        {phase === 'done' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
              {already ? 'Devis déjà validé' : 'Devis validé'}
            </div>
            <div style={{ color: '#9ca3af', fontSize: 14 }}>
              {devis?.devis_number ? `Référence ${devis.devis_number}. ` : ''}
              Merci. Notre équipe revient vers vous pour la suite.
            </div>
          </div>
        )}

        {phase === 'review' && devis && (
          <>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginBottom: 6 }}>Valider votre devis</h2>
            <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 22 }}>Vérifiez les informations puis confirmez votre accord.</p>

            {error && <div style={{ background: '#2a1414', border: '1px solid #5c2626', borderRadius: 12, padding: '10px 14px', color: '#f87171', fontSize: 13, marginBottom: 16 }}>{error}</div>}

            <div style={{ background: '#1c1c1c', borderRadius: 14, padding: 18, marginBottom: 22 }}>
              {devis.devis_number && (
                <Row label="Référence" value={devis.devis_number} />
              )}
              {devis.customer_company && (
                <Row label="Client" value={devis.customer_company} />
              )}
              <Row label="Montant TTC (estimé)" value={fmtMoney(devis.total_ttc_estimated, devis.currency)} strong />
              <Row label="Valable jusqu'au" value={fmtDate(devis.valid_until)} />
            </div>

            <button onClick={handleValidate} disabled={submitting}
              style={{ width: '100%', background: '#C9A24B', color: '#0a0a0a', border: 'none', borderRadius: 12, padding: '13px 0', fontSize: 15, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer' }}>
              {submitting ? 'Validation…' : 'Valider le devis'}
            </button>
            <p style={{ color: '#6b7280', fontSize: 11, marginTop: 14, textAlign: 'center' }}>
              Lien personnel à usage unique. En validant, vous acceptez le devis et nos conditions générales.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
    <span style={{ color: '#9ca3af', fontSize: 13 }}>{label}</span>
    <span style={{ color: strong ? '#C9A24B' : '#e5e7eb', fontSize: strong ? 16 : 14, fontWeight: strong ? 700 : 500 }}>{value}</span>
  </div>
);

export default DevisValidatePage;
